import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";

import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";
import type { ContactAttributesPayload } from "../utils/occupant-payload";

import { useContractContactConstants } from "../hooks/use-contract-contact-constants";
import {
  contactAttributes,
  contactChanges,
  occupantSubscriptions,
  subscriptionPlan,
} from "../utils/occupant-payload";

import { CONTACTS_ENDPOINT, CONTACTS_RESOURCE_TYPE, CONTRACT_CONTACTS_ENDPOINT, RESOURCE_TYPE } from "./endpoints";
import { contractContactsQueryKey } from "./query-keys";
import { fetchOccupantRows } from "./use-occupant-service";

export interface SaveOccupantInput {
  values: OccupantFormValues;
  /** What the drawer was filled with when it opened on an existing contact;
   * omitted when creating. Only what differs from it is written to the contact. */
  initial?: OccupantFormValues;
}

interface SaveOccupantOptions {
  /** Called as soon as a new contact exists, before its alerts are written. A
   * failure after this point leaves the contact in place, so the caller keeps
   * its id and a retry edits that contact rather than creating a second one. */
  onContactCreated?: (contactId: string) => void;
}

/** Creates the person — attached by the backend to the logged-in customer's
 * client — and returns their id. */
const storeContact = async (attributes: ContactAttributesPayload): Promise<string> => {
  const { data } = await axiosInstance().post(CONTACTS_ENDPOINT, {
    data: { type: CONTACTS_RESOURCE_TYPE, attributes },
  });

  const contactId = data?.data?.id;

  if (!contactId) {
    throw new Error("The contact was created but the response carried no id to attach its alerts to.");
  }

  return String(contactId);
};

/** Writes what changed on the person. A destination list that is sent replaces
 * every destination of its kind; one that is left out is untouched. */
const updateContact = (contactId: string, attributes: Partial<ContactAttributesPayload>) =>
  axiosInstance().patch(`${CONTACTS_ENDPOINT}/${contactId}`, {
    data: { type: CONTACTS_RESOURCE_TYPE, id: contactId, attributes },
  });

const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } } | undefined)?.response?.status === 404;

/** Destroys one subscription. One that is already gone is what was asked for,
 * so its 404 is not a failure. */
const destroyRow = async (contractContactId: string) => {
  try {
    await axiosInstance().delete(`${CONTRACT_CONTACTS_ENDPOINT}/${contractContactId}`);
  } catch (error) {
    if (!isNotFound(error)) {
      throw error;
    }
  }
};

/**
 * Saves the Add / Edit contact drawer: the person first, then their alerts on
 * this contract.
 *
 * 1. Create: `POST /contacts` with the name and every destination. Edit:
 *    `PATCH /contacts/{id}` with only what changed — nothing at all when only
 *    boxes were (un)ticked.
 * 2. The contact's rows on the contract are read back AFTER that write — it
 *    decides where each one now delivers (a row following the primary number
 *    moves with it) — and reconciled with the ticked boxes, keyed by alert type
 *    and normalized destination: missing ones are stored, naming their
 *    destination by value, and the ones no longer ticked are destroyed. Reading
 *    them fresh is also what makes a retry after a half-finished save correct.
 *
 * New rows are stored before old ones are destroyed, so a failure midway leaves
 * the contact with the alerts it had rather than with none.
 */
export const useSaveOccupantService = (
  contractId: string | number,
  { onContactCreated }: SaveOccupantOptions = {},
) => {
  const queryClient = useQueryClient();
  const { requireAlertTypeId } = useContractContactConstants();

  return useMutation({
    mutationFn: async ({ values, initial }: SaveOccupantInput) => {
      // Resolved before anything is written: a missing constant fails the save
      // up front instead of after the contact exists.
      const wanted = occupantSubscriptions(values).map((subscription) => ({
        ...subscription,
        alertTypeId: requireAlertTypeId(subscription.alertType),
      }));

      let contactId = values.contactId;

      if (contactId === "") {
        contactId = await storeContact(contactAttributes(values));
        onContactCreated?.(contactId);
      } else {
        const changes = initial ? contactChanges(initial, values) : contactAttributes(values);

        if (Object.keys(changes).length > 0) {
          await updateContact(contactId, changes);
        }
      }

      // A contact created just now has no rows yet.
      const existing = values.contactId === "" ? [] : (await fetchOccupantRows(contractId, contactId)).data;
      const { additions, removals } = subscriptionPlan(existing, wanted);

      await Promise.all(
        additions.map(({ alertTypeId, destination }) =>
          axiosInstance().post(CONTRACT_CONTACTS_ENDPOINT, {
            data: {
              type: RESOURCE_TYPE,
              attributes: { alert_type_id: alertTypeId, destination },
              relationships: {
                contract: { data: { type: "contracts", id: String(contractId) } },
                contact: { data: { type: CONTACTS_RESOURCE_TYPE, id: contactId } },
              },
            },
          }),
        ),
      );

      await Promise.all(removals.map(destroyRow));

      return contactId;
    },
    // On failure too: part of the save may have landed. Everything about the
    // contract is marked stale, but only the list is refetched now — refetching
    // the drawer's own query would re-seed the form under the user. Awaited, so
    // the caller's callbacks (closing, or clearing the form for the next contact)
    // run once the table shows the result.
    onSettled: async () => {
      const queryKey = contractContactsQueryKey(String(contractId));

      await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
      await queryClient.refetchQueries({ queryKey, exact: true });
    },
  });
};
